import { useEffect, useMemo, useState } from "react";
import { EXTRACTION_META, VERDICT_META, type Analysis, type Verdict } from "../../shared/types";
import { fetchPrompts, fetchStats, type Stats } from "../api";
import { DetailModal } from "./DetailModal";
import { LiveClassifier } from "./LiveClassifier";
import { PromptCard } from "./PromptCard";
import { StatStrip } from "./StatStrip";

/** The chip row filters on either axis: harm verdict, or extraction level. */
type Filter = "all" | Verdict | "probing" | "distilling";
const EXTRACTION_FILTERS = ["probing", "distilling"] as const;
const isExtractionFilter = (f: Filter): f is "probing" | "distilling" =>
  f === "probing" || f === "distilling";

/** Cards rendered per page — the corpus is 1000+, so the grid fills in on demand. */
const PAGE = 250;

export function Wall({ onOpenAnalyzer }: { onOpenAnalyzer: () => void }) {
  const [items, setItems] = useState<Analysis[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [filter, setFilter] = useState<Filter>("all");
  const [q, setQ] = useState("");
  const [shown, setShown] = useState(PAGE);
  const [selected, setSelected] = useState<Analysis | null>(null);

  useEffect(() => {
    let alive = true;
    Promise.all([fetchPrompts({ limit: 2000 }), fetchStats()])
      .then(([p, s]) => {
        if (!alive) return;
        setItems(p.items);
        setStats(s);
      })
      .catch(console.error);
    return () => {
      alive = false;
    };
  }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return items.filter((a) => {
      const matchesFilter =
        filter === "all"
          ? true
          : isExtractionFilter(filter)
            ? a.extraction?.level === filter
            : a.verdict === filter;
      return matchesFilter && (!needle || a.text.toLowerCase().includes(needle));
    });
  }, [items, filter, q]);

  const counts = useMemo(() => {
    const c: Record<Filter, number> = {
      all: items.length,
      safe: 0,
      suspicious: 0,
      malicious: 0,
      probing: 0,
      distilling: 0,
    };
    for (const a of items) {
      c[a.verdict]++;
      const lvl = a.extraction?.level;
      if (lvl === "probing" || lvl === "distilling") c[lvl]++;
    }
    return c;
  }, [items]);

  // Any change to the query resets pagination.
  useEffect(() => setShown(PAGE), [filter, q]);

  const visible = useMemo(() => filtered.slice(0, shown), [filtered, shown]);
  const hasMore = filtered.length > visible.length;

  const blocked = (stats?.byVerdict.malicious ?? 0) + (stats?.byVerdict.suspicious ?? 0);
  const cleared = stats?.byVerdict.safe ?? 0;
  const extractionFlagged =
    (stats?.byExtraction?.probing ?? 0) + (stats?.byExtraction?.distilling ?? 0);

  return (
    <>
      {/* ── Hero ─────────────────────────────────────────────── */}
      <section className="field-pink border-b-[1.5px] border-ink">
        <div className="relative mx-auto max-w-[1400px] px-5 py-10 sm:px-8 sm:py-14">
          <div className="crop px-2 py-2">
            <div className="mono mb-3 text-[10.5px] tracking-[0.1em]">
              FENZ.AI / GUARDRAILS ·········· PROMPT CLASSIFICATION
            </div>

            <h1 className="display text-[clamp(44px,10.5vw,132px)]">
              {blocked} BLOCKED,
              <br />
              {cleared} CLEARED.
            </h1>

            <div className="mono mt-4 text-[11px]">
              *{stats?.total ?? 0} prompts screened ·{" "}
              {stats?.medianLatencyMs ?? 0}ms median ·{" "}
              <span style={{ color: EXTRACTION_META.distilling.accent }}>
                {extractionFlagged} extraction
              </span>{" "}
              <button onClick={onOpenAnalyzer} className="underline underline-offset-2 hover:bg-ink hover:text-paper">
                (classify your own)
              </button>
            </div>
          </div>

          <div className="mt-8 grid gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
            <div className="min-w-0">
              <LiveClassifier items={items} />
            </div>
            {stats && <StatStrip stats={stats} />}
          </div>
        </div>
      </section>

      {/* ── Feed ─────────────────────────────────────────────── */}
      <section className="mx-auto max-w-[1400px] px-5 pb-20 sm:px-8">
        <div className="sticky top-10 z-20 -mx-5 flex flex-wrap items-stretch border-b-[1.5px] border-ink bg-paper px-5 sm:-mx-8 sm:px-8">
          {(["all", "safe", "suspicious", "malicious"] as const).map((f) => {
            const active = filter === f;
            const accent = f === "all" ? "var(--ink)" : VERDICT_META[f].accent;
            return (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className="mono border-r-[1.5px] border-ink px-3 py-2 text-[11px] uppercase tracking-[0.08em]"
                style={{
                  background: active ? accent : "transparent",
                  color: active && f !== "suspicious" ? "var(--paper)" : "var(--ink)",
                }}
              >
                {f} <span className="tabular-nums opacity-70">{counts[f]}</span>
              </button>
            );
          })}
          {EXTRACTION_FILTERS.map((f) => {
            const active = filter === f;
            const accent = EXTRACTION_META[f].accent;
            return (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className="mono border-r-[1.5px] border-ink px-3 py-2 text-[11px] uppercase tracking-[0.08em]"
                style={{
                  background: active ? accent : "transparent",
                  color: active ? "var(--paper)" : accent,
                }}
                title={`extraction: ${f}`}
              >
                ⧉ {f} <span className="tabular-nums opacity-70">{counts[f]}</span>
              </button>
            );
          })}
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="grep…"
            className="mono min-w-0 flex-1 bg-transparent px-3 py-2 text-[11px] outline-none placeholder:opacity-40"
          />
          <span className="mono hidden items-center px-3 py-2 text-[11px] opacity-50 sm:flex">
            {filtered.length}/{items.length}
          </span>
        </div>

        <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5">
          {visible.map((a, i) => (
            <PromptCard key={a.id} a={a} index={i % PAGE} onClick={() => setSelected(a)} />
          ))}
        </div>

        {filtered.length === 0 && (
          <div className="mono py-20 text-center text-[12px] opacity-50">no matches</div>
        )}

        {hasMore && (
          <button
            onClick={() => setShown((n) => n + PAGE)}
            className="mono mt-4 w-full border-[1.5px] border-ink bg-paper py-3 text-[11px] uppercase tracking-[0.1em] hover:bg-pink"
          >
            load more · {filtered.length - visible.length} remaining
          </button>
        )}
      </section>

      <DetailModal a={selected} onClose={() => setSelected(null)} />
    </>
  );
}
